/**
 * Phase 7-B — 3-step hire flow (Review → Sign → Locked).
 *
 * Spec: 02_api_spec.md §5.1 (quote) + §5.2 (hire) + §10 (wallet sub-key).
 * EIP-712 helper: @infinia/ida-sdk → buildHireOrderTypedData.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import { apiFetch } from '../../api/client';
import { buildHireOrderTypedData, type Quote, type WalletPubkey } from '@infinia/ida-sdk';
import type { MarketplaceListing } from '@infinia/ida-sdk';
import { weiToAdi, shortAddr, uuid } from './utils';
import { encodePathSegment } from '../../lib/url';

interface HireModalProps {
  open: boolean;
  onClose: () => void;
  listing: MarketplaceListing;
  buyerDid?: string;
}

type Step = 'review' | 'sign' | 'locked';

interface HireResponseLite {
  escrowId: string;
  status: string;
  txHash: string;
}

export default function HireModal({ open, onClose, listing, buyerDid }: HireModalProps) {
  const { t } = useTranslation(['marketplace', 'common']);
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('review');
  const [ttl, setTtl] = useState<number>(3600);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [pubkey, setPubkey] = useState<WalletPubkey | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [hireRes, setHireRes] = useState<HireResponseLite | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset whenever modal opens.
  useEffect(() => {
    if (open) {
      setStep('review');
      setQuote(null);
      setPubkey(null);
      setSignature(null);
      setHireRes(null);
      setError(null);
      setTtl(3600);
    }
  }, [open]);

  const mapError = (msg: string): string => {
    const m = msg.toLowerCase();
    if (m.includes('insufficient')) return t('hire.errors.insufficientAdi');
    if (m.includes('subkey_not_provisioned')) return t('hire.errors.subkeyNotProvisioned');
    if (m.includes('subkey_desync')) return t('hire.errors.subkeyDesync');
    if (m.includes('deadline_past')) return t('hire.errors.deadlinePast');
    if (m.includes('fee_bps_mismatch')) return t('hire.errors.feeMismatch');
    if (m.includes('listing_paused')) return t('hire.errors.listingPaused');
    if (m.includes('listing_scope_changed')) return t('hire.errors.listingScopeChanged');
    if (m.includes('nonce_used')) return t('hire.errors.nonceUsed');
    if (m.includes('signature')) return t('hire.errors.signatureRejected');
    if (m.includes('network') || m.includes('fetch')) return t('hire.errors.network');
    return msg;
  };

  // ---- Step 1 → quote ----
  const fetchQuote = async () => {
    if (!buyerDid) {
      setError(t('hire.errors.subkeyNotProvisioned'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const q = await apiFetch<Quote>(`/marketplace/listings/${encodePathSegment(listing.id)}/quote`, {
        method: 'POST',
        headers: { 'Idempotency-Key': uuid() },
        body: JSON.stringify({ buyerDid, ibctTtlSecondsRequested: ttl }),
      });
      setQuote(q);
      // Pre-fetch the marketplace pubkey so the sign step is instant.
      try {
        const pk = await apiFetch<WalletPubkey>('/wallets/me/marketplace-pubkey');
        setPubkey(pk);
      } catch {
        // Best-effort. If absent, the sign step will surface the error.
      }
      setStep('sign');
    } catch (e) {
      setError(mapError(e instanceof Error ? e.message : 'quote_failed'));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 2 → sign + hire ----
  const signAndHire = async () => {
    if (!quote) return;
    setBusy(true);
    setError(null);
    try {
      // Build typed-data (deterministic; hire-order from the API).
      const typedData = buildHireOrderTypedData(quote.hireOrder, quote.domain);

      // Server-mediated EIP-712 sign — wallet holds the secp sub-key.
      const signRes = await apiFetch<{ signature: string; signer: string }>(
        '/wallets/me/sign-eip712',
        { method: 'POST', body: JSON.stringify({ typedData }) },
      );
      setSignature(signRes.signature);

      const hireRaw = await apiFetch<HireResponseLite>(
        `/marketplace/listings/${encodePathSegment(listing.id)}/hire`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': uuid() },
          body: JSON.stringify({ order: quote.hireOrder, signature: signRes.signature }),
        },
      );
      setHireRes(hireRaw);
      setStep('locked');
    } catch (e) {
      setError(mapError(e instanceof Error ? e.message : 'sign_failed'));
    } finally {
      setBusy(false);
    }
  };

  const rotateKey = async () => {
    setBusy(true);
    setError(null);
    try {
      const pk = await apiFetch<WalletPubkey>('/wallets/me/secp-key/rotate', { method: 'POST' });
      setPubkey(pk);
    } catch (e) {
      setError(mapError(e instanceof Error ? e.message : 'rotate_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('hire.title')} wide>
      {/* Step indicator */}
      <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide">
        {(['review', 'sign', 'locked'] as Step[]).map((s, i) => {
          const active = step === s;
          const done =
            (step === 'sign' && s === 'review') ||
            (step === 'locked' && (s === 'review' || s === 'sign'));
          return (
            <div key={s} className="flex items-center gap-1">
              <span
                className={`flex size-6 items-center justify-center rounded-full text-[10px] ${
                  done ? 'bg-emerald-500 text-white' : active ? 'bg-primary text-white' : 'bg-slate-100 text-text-faint'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              <span className={active || done ? 'text-text-main' : 'text-text-faint'}>
                {t(`hire.step${i + 1}Title`)}
              </span>
              {i < 2 && <span className="mx-1 text-text-faint">→</span>}
            </div>
          );
        })}
      </div>

      {/* Step 1: review + ttl + quote */}
      {step === 'review' && (
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            {t('hire.review.summary', { price: weiToAdi(listing.pricing.priceWei) })}
          </p>
          <div className="space-y-2 rounded-lg border border-border-subtle bg-slate-50 p-3 text-xs">
            <KvRow label={t('hire.review.price')} value={`${weiToAdi(listing.pricing.priceWei)} ADI`} />
            <KvRow label={t('hire.review.ibctScope')} value={listing.capabilityScope} mono />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-text-muted">{t('hire.review.ttlLabel')}</label>
            <input
              type="number"
              min="60"
              step="60"
              value={ttl}
              onChange={(e) => setTtl(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-border-subtle bg-white px-2 text-sm"
            />
          </div>
          {error && <p className="text-xs font-semibold text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button onClick={fetchQuote} disabled={busy} className="btn-primary text-xs disabled:opacity-50">
              {busy ? t('hire.review.loading') : t('hire.review.continue')}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: sign */}
      {step === 'sign' && quote && (
        <div className="space-y-4">
          <div className="space-y-2 rounded-lg border border-border-subtle bg-slate-50 p-3 text-xs">
            <KvRow label={t('hire.review.price')} value={`${weiToAdi(quote.priceWei)} ADI`} />
            <KvRow label={t('hire.review.fee', { bps: quote.feeBps })} value={`${weiToAdi(quote.feeWei)} ADI`} />
            <KvRow label={t('hire.review.operatorPayout')} value={`${weiToAdi(quote.operatorPayoutWei)} ADI`} />
            <KvRow label={t('hire.review.ibctScope')} value={quote.ibctScope} mono />
            <KvRow label={t('hire.review.ibctTtl')} value={`${quote.ibctTtlSeconds}s`} />
            <KvRow label={t('hire.review.expiresAt')} value={quote.expiresAt} />
          </div>

          <div className="rounded-lg border border-border-subtle p-3">
            <p className="mb-2 text-xs font-bold text-text-muted">{t('hire.sign.key')}</p>
            <p className="font-mono text-[11px] text-text-main">
              {pubkey ? shortAddr(pubkey.address, 8) : t('hire.sign.fetchKey')}
            </p>
          </div>

          <p className="text-xs text-text-faint">{t('hire.sign.explainer')}</p>
          {error && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-red-500">{error}</p>
              {error === t('hire.errors.subkeyNotProvisioned') && (
                <button onClick={rotateKey} disabled={busy} className="btn-secondary text-xs">
                  {t('hire.sign.rotateKey')}
                </button>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={() => setStep('review')} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:back')}
            </button>
            <button onClick={signAndHire} disabled={busy} className="btn-primary text-xs disabled:opacity-50">
              {busy ? t('hire.sign.signing') : t('hire.sign.sign')}
            </button>
          </div>
          {signature && (
            <p className="text-[10px] font-mono text-text-faint break-all">sig: {shortAddr(signature, 10)}</p>
          )}
        </div>
      )}

      {/* Step 3: locked */}
      {step === 'locked' && hireRes && (
        <div className="space-y-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <div className="mb-1 flex items-center gap-2 font-bold text-emerald-700">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
              {t('hire.locked.title')}
            </div>
            <p className="text-xs text-emerald-700">{t('hire.locked.subtitle')}</p>
          </div>

          <div className="space-y-2 rounded-lg border border-border-subtle bg-slate-50 p-3 text-xs">
            <KvRow label={t('hire.locked.escrowId')} value={shortAddr(hireRes.escrowId, 8)} mono />
            <KvRow label={t('hire.locked.txHash')} value={shortAddr(hireRes.txHash, 8)} mono />
          </div>
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('hire.locked.close')}
            </button>
            <button
              onClick={() => { onClose(); navigate('/operators/me/escrows'); }}
              className="btn-primary text-xs"
            >
              {t('hire.locked.viewEscrow')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function KvRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-text-muted">{label}</span>
      <span className={mono ? 'font-mono text-text-main' : 'font-bold text-text-main'}>{value}</span>
    </div>
  );
}
